import React, { useMemo } from 'react';
import { Image, Platform, TextInput } from 'react-native';
import {
  Colors,
  ColorValues,
  convertHexToBackgroundColorKeys,
  convertHexToBorderColorKeys,
  convertHexToColorKeys,
  Divider,
  Flex,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import { bridge, isNil, isNotNil, localizeNumber } from 'shared/src/utils';
import { useGoodsBasic } from '../../queries/useGoodsBasic';
import { usePreRenderParams } from '../../hooks/usePreRenderParams';
import { ShareRewardButton } from './components/ShareRewardButton';
import { useGoodsDetail } from '../../queries/useGoodsDetail';
import { TodayDeliveryChip } from './components/TodayDeliveryChip';
import { isDeliveryTypeIsToday } from '../../utils/isDeliveryTypeIsToday';
import { isSalesMethodIsPreOrder } from '../../utils/isSalesMethodIsPreOrder';
import { parseNudgeText } from './utils/parseNudgeText';

type GoodsInfoProps = {
  isSkeleton?: boolean;
};

export const GoodsInfo: React.FC<GoodsInfoProps> = React.memo(() => {
  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();

  const { data: goodsBasicSkeleton } = usePreRenderParams();

  const goodsInfo = useMemo(() => {
    if (isNotNil(goodsBasic)) {
      return {
        name: goodsBasic.name,
        price_description: goodsBasic.price_description,
        discount_rate: goodsBasic.price_info.discount_rate,
        thumbnail_price: goodsBasic.price_info.thumbnail_price,
        consumer_price: goodsBasic.price_info.consumer,
        base_thumbnail_price:
          goodsBasic.price_info.base_thumbnail_price ?? null,
      };
    }
    return {
      name: goodsBasicSkeleton?.name ?? '',
      price_description: null,
      discount_rate: goodsBasicSkeleton?.price_info.discount_rate ?? 0,
      thumbnail_price: goodsBasicSkeleton?.price_info.thumbnail_price ?? 0,
      consumer_price: goodsBasicSkeleton?.price_info.consumer ?? 0,
      base_thumbnail_price: null as number | null,
    };
  }, [goodsBasic, goodsBasicSkeleton]);

  const showNudging = isNotNil(goodsInfo.price_description?.nudge_text);

  const parsedNudge = useMemo(() => {
    if (!showNudging || !goodsInfo.price_description?.nudge_text) return null;
    return parseNudgeText(goodsInfo.price_description.nudge_text);
  }, [showNudging, goodsInfo.price_description?.nudge_text]);

  const nudgeTextColor =
    goodsInfo.price_description?.nudge_text_color ||
    Colors.content_pink_primary;
  const nudgeBgColor =
    goodsInfo.price_description?.nudge_background_color || '#FFDFE4';
  const nudgeAreaBgColor =
    goodsInfo.price_description?.nudge_area_background_color || '#FFF4F6';

  if (isNil(goodsBasic)) return null;

  return (
    <Flex>
      <Divider size={'small'} />
      <Flex padding={{ horizontal: 16, vertical: 16 }} gap={12}>
        <Flex.Row gap={4} alignItems={'center'}>
          {!isSalesMethodIsPreOrder(goodsBasic) &&
            isDeliveryTypeIsToday(goodsBasic) && (
              <TodayDeliveryChip
                hourStr={
                  goodsBasic?.market.today_delivery_order_deadline_time ?? null
                }
              />
            )}
          {goodsDetail?.chips.map(chip => {
            if (chip.type === 'PRE_ORDER' || chip.type === 'THIRD_PL') {
              return (
                <Flex key={chip.name}>
                  <RemoteImage
                    uri={chip.meta.url}
                    width={20 * chip.meta.ratio}
                    height={20}
                  />
                </Flex>
              );
            }
            if (chip.type === 'DITTO_IMAGE') {
              return (
                <Flex
                  key={chip.name}
                  onPress={
                    isNotNil(chip.display_info)
                      ? () => bridge.toast(chip.display_info!)
                      : undefined
                  }
                >
                  <RemoteImage
                    uri={chip.meta.url}
                    width={20 * chip.meta.ratio}
                    height={20}
                  />
                </Flex>
              );
            }
            if (chip.type === 'DITTO_TEXT') {
              return (
                <Flex.Center
                  key={chip.name}
                  borderWidth={chip.meta.border_width}
                  borderRadius={chip.meta.rounded_corner_radius}
                  borderColor={
                    convertHexToBorderColorKeys(chip.meta.border_color) ??
                    undefined
                  }
                  height={20}
                  borderColorValue={
                    convertHexToBorderColorKeys(chip.meta.border_color) === null
                      ? chip.meta.border_color
                      : undefined
                  }
                  backgroundColor={
                    convertHexToBackgroundColorKeys(chip.meta.bg_color) ??
                    undefined
                  }
                  backgroundColorValue={
                    convertHexToBackgroundColorKeys(chip.meta.bg_color) === null
                      ? chip.meta.bg_color
                      : undefined
                  }
                  padding={{
                    horizontal: 4,
                  }}
                >
                  <Typography
                    color={
                      convertHexToColorKeys(chip.meta.text_color) ?? undefined
                    }
                    rawColor={
                      convertHexToColorKeys(chip.meta.text_color)
                        ? undefined
                        : chip.meta.text_color
                    }
                    type={chip.meta.text_style}
                  >
                    {chip.name}
                  </Typography>
                </Flex.Center>
              );
            }
          })}
        </Flex.Row>
        <TextInput
          value={goodsInfo.name}
          editable={false}
          multiline
          scrollEnabled={false}
          style={{
            fontSize: 16,
            lineHeight: 20,
            fontFamily: Platform.select({
              ios: 'Pretendard-Regular',
              android: 'pretendard_regular',
            }),
            letterSpacing: -0.4,
            color: ColorValues.gray70,
            padding: 0,
          }}
        />
        {isNotNil(goodsDetail) &&
          isNotNil(goodsDetail.product_info_provision_notice) &&
          isNotNil(goodsDetail.product_info_provision_notice.country) &&
          goodsDetail.product_info_provision_notice.country !== '' && (
            <Flex margin={{ top: -8 }}>
              <Typography type={'body3'} rawColor={ColorValues.gray70}>
                원산지 : {goodsDetail.product_info_provision_notice.country}
              </Typography>
            </Flex>
          )}
        {showNudging ? (
          <Flex
            backgroundColor={'pink3'}
            backgroundColorValue={nudgeAreaBgColor}
            borderRadius={8}
            padding={{ horizontal: 12, vertical: 12 }}
          >
            {isNotNil(goodsInfo.price_description?.text) &&
              goodsInfo.price_description.text.length > 0 && (
                <Flex.Row alignItems={'center'} gap={4}>
                  <Flex.Row gap={2}>
                    {isNotNil(goodsInfo.price_description.icon_url) && (
                      // eslint-disable-next-line no-restricted-syntax -- tintColor 필요, RemoteImage 미지원
                      <Image
                        source={{ uri: goodsInfo.price_description.icon_url }}
                        style={{
                          width: 16,
                          height: 16,
                          tintColor: nudgeTextColor,
                        }}
                      />
                    )}
                    <Typography
                      type={'subtitle4'}
                      rawColor={'rgba(0,0,0,0.5)'}
                      numberOfLines={1}
                    >
                      {goodsInfo.price_description.text}
                    </Typography>
                  </Flex.Row>
                  {goodsInfo.discount_rate > 0 && (
                    <Flex margin={{ left: 2 }}>
                      <Typography
                        type={'body2'}
                        rawColor={'rgba(0,0,0,0.2)'}
                        textDecorationLine={'line-through'}
                      >
                        {localizeNumber(goodsInfo.consumer_price ?? 0)}원
                      </Typography>
                    </Flex>
                  )}
                </Flex.Row>
              )}
            {isNotNil(goodsInfo.base_thumbnail_price) && (
              <Flex
                margin={{
                  top: isNotNil(goodsInfo.price_description?.text) ? 2 : 0,
                }}
              >
                <Typography type={'h5'} rawColor={'rgba(0,0,0,0.5)'}>
                  {localizeNumber(goodsInfo.base_thumbnail_price)}원
                </Typography>
              </Flex>
            )}
            <Flex.Row
              alignItems={'center'}
              justifyContent={'space-between'}
              margin={{ top: 4 }}
            >
              <Flex.Row gap={4}>
                {goodsInfo.discount_rate > 0 && (
                  <Typography
                    type={'h4'}
                    color={'content_pink_primary'}
                    rawColor={nudgeTextColor}
                  >
                    {goodsInfo.discount_rate}%
                  </Typography>
                )}
                <Typography type={'h4'}>
                  {localizeNumber(goodsInfo.thumbnail_price)}원
                </Typography>
              </Flex.Row>
              <ShareRewardButton
                showNudging={showNudging}
                nudgeTextColor={nudgeTextColor}
              />
            </Flex.Row>
            {isNotNil(parsedNudge) && (
              <>
                <Flex
                  height={1}
                  backgroundColorValue={Colors.border_transparent}
                  margin={{ top: 12 }}
                />
                <Flex.Row alignItems={'center'} gap={4} margin={{ top: 12 }}>
                  {isNotNil(
                    goodsInfo.price_description?.nudge_condition_text,
                  ) && (
                    <Typography type={'subtitle3'} rawColor={'rgba(0,0,0,0.5)'}>
                      {goodsInfo.price_description!.nudge_condition_text}
                    </Typography>
                  )}
                  <Flex.Row
                    backgroundColor={'pink10'}
                    backgroundColorValue={nudgeBgColor}
                    borderRadius={4}
                    padding={{ horizontal: 4, vertical: 2 }}
                  >
                    <Typography
                      type={'subtitle3'}
                      color={'content_pink_primary'}
                      rawColor={nudgeTextColor}
                    >
                      {parsedNudge.boldText}
                    </Typography>
                    <Typography
                      type={'subtitle3'}
                      color={'content_pink_primary'}
                      rawColor={nudgeTextColor}
                    >
                      {parsedNudge.normalText}
                    </Typography>
                  </Flex.Row>
                </Flex.Row>
              </>
            )}
          </Flex>
        ) : (
          <Flex gap={2}>
            <Flex.Row alignItems={'center'} gap={4}>
              {goodsInfo.price_description && (
                <Flex.Row alignItems={'center'} gap={2}>
                  {isNotNil(goodsInfo.price_description.icon_url) && (
                    <RemoteImage
                      uri={goodsInfo.price_description.icon_url}
                      width={16}
                      height={16}
                    />
                  )}
                  <Typography
                    type={'subtitle4'}
                    color={'content_secondary'}
                    numberOfLines={1}
                  >
                    {goodsInfo.price_description.text}
                  </Typography>
                </Flex.Row>
              )}
              {goodsInfo.discount_rate > 0 && (
                <Typography
                  type={'body2'}
                  color={'content_disabled'}
                  textDecorationLine={'line-through'}
                >
                  {localizeNumber(goodsInfo.consumer_price ?? 0)}원
                </Typography>
              )}
            </Flex.Row>
            <Flex.Row alignItems={'center'} justifyContent={'space-between'}>
              <Flex.Row gap={4}>
                {goodsInfo.discount_rate > 0 && (
                  <Typography type={'h4'} color={'content_pink_primary'}>
                    {goodsInfo.discount_rate}%
                  </Typography>
                )}
                <Typography type={'h4'}>
                  {localizeNumber(goodsInfo.thumbnail_price)}원
                </Typography>
              </Flex.Row>
              <ShareRewardButton showNudging={showNudging} />
            </Flex.Row>
          </Flex>
        )}
      </Flex>
      <Divider size={'small'} />
    </Flex>
  );
});
